import {
  Box,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";

export interface DeleteAppQueueDialogState {
  isOpen: boolean;
  processed: number;
  total: number;
  failed: number;
  activeAppName?: string;
  language: SupportedLanguage;
}

export const DeleteAppQueueDialog: PimoReactComponent<
  DeleteAppQueueDialogState
> = ({ isOpen, processed, total, failed, activeAppName, language }) => {
  const theme = useTheme();
  const progressValue =
    total > 0 ? Math.min(100, Math.max(0, (processed / total) * 100)) : 0;

  if (!isOpen) return null;

  return (
    <Paper
      role="status"
      aria-live="polite"
      elevation={6}
      sx={{
        position: "fixed",
        bottom: theme.spacing(2),
        right: theme.spacing(2),
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        maxWidth: `calc(100vw - ${theme.spacing(4)})`,
        zIndex: theme.zIndex.appBar,
        pointerEvents: "none",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          gap={2}
        >
          <Typography component="h2" variant="h6" sx={{ fontWeight: 600 }}>
            {getLocalizedMessage(
              language,
              "homePortalPage.deleteQueueDialog.title"
            )}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {getLocalizedMessage(
              language,
              "homePortalPage.deleteQueueDialog.progress",
              { processed, total }
            )}
          </Typography>
        </Stack>
        <LinearProgress
          variant={total > 0 ? "determinate" : "indeterminate"}
          value={progressValue}
          sx={{
            height: theme.spacing(0.75),
            borderRadius: 999,
            backgroundColor: theme.palette.action.hover,
          }}
        />
        {activeAppName && (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              backgroundColor: theme.palette.action.hover,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {getLocalizedMessage(
                language,
                "homePortalPage.deleteQueueDialog.activeApp"
              )}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {activeAppName}
            </Typography>
          </Box>
        )}
        {failed > 0 && (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
              backgroundColor: alpha(theme.palette.warning.main, 0.12),
            }}
          >
            <Typography variant="body2" color="warning.dark">
              {getLocalizedMessage(
                language,
                "homePortalPage.deleteQueueDialog.failed"
              )}
              {`: ${failed}`}
            </Typography>
          </Box>
        )}
        <Typography variant="body2" color="text.secondary">
          {getLocalizedMessage(
            language,
            "homePortalPage.deleteQueueDialog.progress",
            { processed, total }
          )}
        </Typography>
      </Stack>
    </Paper>
  );
};
